import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';

/** In-list pending state keeps six rows so pagination resolve does not jump. */
export const DISCOVER_LIST_SKELETON_ROW_COUNT = 6;

/**
 * Route-level loading (DIS-6 / WEB-940) uses three rows — the v3 loading
 * frames — while still sharing the live `.dv-ev` grid.
 */
export const DISCOVER_ROUTE_SKELETON_ROW_COUNT = 3;

export const DISCOVER_SKELETON_BLOCK_CLASS = 'motion-safe:animate-pulse rounded-md bg-loci-border';

const BLOCK = DISCOVER_SKELETON_BLOCK_CLASS;

export interface DiscoverListSkeletonProps {
  /** Number of `.dv-ev` placeholder rows. */
  readonly rowCount?: number;
  /** Test id. Defaults to `discover-list-view-skeleton`. */
  readonly 'data-testid'?: string;
}

/**
 * Server-safe Discover list placeholder (WEB-940).
 *
 * No `'use client'`, no ThemeProvider. Rows opt into the live `.dv-ev` grid
 * (`112px minmax(0,1fr) auto`, `minmax(0,1fr) auto` at ≤720) so the skeleton
 * cannot drift from `DiscoverEventCard`. Pulse is `motion-safe:` so
 * `prefers-reduced-motion: reduce` settles to static bones.
 */
export function DiscoverListSkeleton({
  rowCount = DISCOVER_LIST_SKELETON_ROW_COUNT,
  'data-testid': dataTestId = 'discover-list-view-skeleton',
}: DiscoverListSkeletonProps): React.ReactElement {
  return (
    <div
      className="flex w-full flex-col gap-0"
      data-testid={dataTestId}
      aria-busy="true"
      aria-live="polite"
    >
      {/* WEB-1075 — day-head placeholder mirrors the slim one-line head: a
          ~34px numeral bone + one 16px label line on the live
          `.disc-day__head` geometry (replaces the retired 48px bone).
          WEB-1083 — the numeral drops to 28px at ≤720 like the live
          `.disc-day__number` (`styles/surfaces/discover.css`). */}
      <div
        className="disc-day__head disc-day__head--bone border-b border-loci-border"
        data-testid="discover-list-skeleton-day-head"
      >
        <div className="disc-day__bone-row" data-testid="discover-list-skeleton-day-head-bones">
          <div className={cn(BLOCK, 'disc-day__bone-number')} />
          <div className={cn(BLOCK, 'h-4 w-40 max-w-[60%]')} />
        </div>
      </div>
      {Array.from({ length: rowCount }).map((_, index) => (
        // WEB-670 / WEB-940 — reuse the live row's `.dv-ev` grid rather than
        // restating geometry in Tailwind (`grid-cols-[90px_1fr]` was the
        // retired pre-v2 template and snaps when real rows stream in).
        //
        // WEB-1083 — two bone sets share the row. The desktop bones
        // (`.dv-skel__d`, hidden at ≤720) are unchanged; the other `.dv-skel__*`
        // bones paint only at ≤720 and mirror the one-column row's line boxes
        // (`styles/surfaces/discover.css`), so the first real row lands where
        // its bone sat. No pill or why-line bones: those lines render only
        // when present.
        <div key={index} className="dv-ev" data-testid="discover-list-skeleton-row">
          <div className="dv-ev__t">
            <div className={cn(BLOCK, 'size-11 rounded-lg dv-skel__d')} />
            <div className={cn(BLOCK, 'h-2 w-20 dv-skel__d')} />
            <div className={cn(BLOCK, 'mt-1 h-6 w-16 dv-skel__d')} />
            <div className={cn(BLOCK, 'dv-skel__glyph')} />
            <div className={cn(BLOCK, 'dv-skel__time')} />
            <div className={cn(BLOCK, 'dv-skel__hood')} />
          </div>
          <div className="dv-skel__body dv-skel__d">
            <div className={cn(BLOCK, 'h-4 w-3/4')} />
            <div className={cn(BLOCK, 'h-3 w-1/2')} />
          </div>
          <div className="dv-ev__r">
            <div className={cn(BLOCK, 'size-11 rounded-full dv-skel__d')} />
          </div>
          <div className={cn(BLOCK, 'dv-skel__price')} />
          <div className={cn(BLOCK, 'dv-skel__title')} />
          <div className={cn(BLOCK, 'dv-skel__meta')} />
          <div className={cn(BLOCK, 'dv-skel__cat')} />
          <div className="dv-skel__actions">
            <div className={cn(BLOCK, 'dv-skel__cta')} />
            <div className={cn(BLOCK, 'dv-skel__save rounded-full')} />
          </div>
        </div>
      ))}
    </div>
  );
}
